import { useState, useCallback } from "react";
import { Mic, MicOff, Maximize, Minimize, PhoneOff } from "lucide-react";
import { Room } from "livekit-client";

interface SessionControlsProps {
  room: Room | null;
  isVideoEnabled: boolean;
  onEndSession: () => void;
}

export default function SessionControls({
  room,
  isVideoEnabled,
  onEndSession,
}: SessionControlsProps) {
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleMute = useCallback(async () => {
    if (!room) return;

    try {
      const newMutedState = !isMuted;
      await room.localParticipant.setMicrophoneEnabled(!newMutedState);
      setIsMuted(newMutedState);
      console.log(`Microphone ${newMutedState ? "muted" : "unmuted"}`);
    } catch (error) {
      console.error("Error toggling mute:", error);
    }
  }, [room, isMuted]);

  const toggleFullscreen = useCallback(() => {
    try {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      } else {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error("Error toggling fullscreen:", error);
    }
  }, []);

  return (
    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-3 bg-black/50 backdrop-blur-sm px-4 py-3 rounded-full">
      {/* Mute Button */}
      <button
        onClick={toggleMute}
        className="btn btn-circle btn-sm bg-white/20 hover:bg-white/30 border-none text-white"
        aria-label={isMuted ? "Unmute Your Microphone" : "Mute Your Microphone"}
      >
        {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
      </button>

      {/* Fullscreen Button - only show in video mode */}
      {isVideoEnabled && (
        <button
          onClick={toggleFullscreen}
          className="btn btn-circle btn-sm bg-white/20 hover:bg-white/30 border-none text-white"
          aria-label={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
        >
          {isFullscreen ? (
            <Minimize className="w-5 h-5" />
          ) : (
            <Maximize className="w-5 h-5" />
          )}
        </button>
      )}

      {/* End Call Button */}
      <button
        onClick={onEndSession}
        className="btn btn-circle btn-sm bg-red-500/80 hover:bg-red-600/90 border-none text-white"
        aria-label="End Call"
      >
        <PhoneOff className="w-5 h-5" />
      </button>
    </div>
  );
}
